import { Injectable } from '@angular/core';

import { AccaBoostConfig } from '@frontend/sports/common/client-config-data-access';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { Odds, OddsComparerFactory, OddsOperations } from '@frontend/sports/odds/feature';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { isUndefined } from 'lodash-es';
import { Observable, distinctUntilChanged, map, pairwise, share } from 'rxjs';

import { selectIsLinearBetslip } from '../../../base/store/selectors';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipType } from '../../../core/betslip-type';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { selectSelectedPicksByType } from '../../reward-tokens/components/digital/cards/odds-boost/odds-boost-card.component';
import { AccaBoostToken, AccaBoostTokenMinOdds } from '../../reward-tokens/reward-tokens.model';
import { selectRewardTokens, selectTokensEligibilityState } from '../../reward-tokens/selectors';
import { findMinimumOddsAccaTokens } from '../../reward-tokens/services/reward-tokens.utils';
import { IRewardTokenEligibility, IRewardTokenEligibilityState } from '../../reward-tokens/state';
import { betslipCurrentTypeSelector } from '../../types/base/selectors';
import {
    AccaBoostContext,
    AccaBoostError,
    ErrorNotification,
    ErrorsMap,
    INotification,
    LadderNotification,
    MaxRewardNotification,
    OneSelectionAwayNotification,
} from '../model';
import { AccaBoostContextService } from './acca-boost-context.service';

export interface HandlerData {
    context: Nullable<AccaBoostContext>;
    maxRewardAchieved: boolean;
}

@Injectable()
export class AccaBoostNotificationHandlerService {
    readonly notification$: Observable<INotification | null>;

    constructor(
        store: Store<IBetslipRootState>,
        contextService: AccaBoostContextService,
        private accaBoostConfig: AccaBoostConfig,
        private oddsComparerFactory: OddsComparerFactory,
    ) {
        const context$ = contextService.context$.pipe(pairwise());
        const tokensEligibilityState$ = store.select(selectTokensEligibilityState);
        const currentBetslipType$ = store.select(betslipCurrentTypeSelector);
        const isLinear$ = store.select(selectIsLinearBetslip);
        const rewardTokens$ = store.select(selectRewardTokens);
        const selectedPicks$ = store.select(selectSelectedPicksByType(BetslipType.Combo));

        this.notification$ = context$.pipe(
            concatLatestFrom(() => [tokensEligibilityState$, currentBetslipType$, isLinear$, rewardTokens$, selectedPicks$]),
            map(([[previous, current], eligibilityState, currentBetslipType, isLinear, rewardTokens, selectedPicks]) => {
                const maxRewardAchieved = !previous.maxReward && current.maxReward;
                const pickCountChanged = previous.picksCount !== current.picksCount;
                if (!isUndefined(current.token) && current.isOneLegAway) {
                    return new OneSelectionAwayNotification(current.token, this.accaBoostConfig.oneSelectionAwayNotificationDuration);
                }

                // If we switch tabs from combo to single/teaser we need to display an error
                // Acca boost will still be valid for combo
                // Therefore we need to check the other types for eligibility
                if (
                    !isUndefined(previous.token) &&
                    previous.isSelected &&
                    !isUndefined(current.token) &&
                    !current.isSelected &&
                    !isLinear &&
                    (currentBetslipType === BetslipType.Single ||
                        currentBetslipType === BetslipType.Teaser ||
                        currentBetslipType === BetslipType.System)
                ) {
                    const error = this.resolveErrorForOtherTypes(eligibilityState, current, currentBetslipType);

                    return error ? new ErrorNotification(current.token, error, this.accaBoostConfig.errorNotificationDuration) : null;
                }

                if (current.picksCount > 0 && previous.picksCount === 0 && rewardTokens) {
                    const tokenData = findMinimumOddsAccaTokens(eligibilityState, rewardTokens);

                    if (tokenData) {
                        const validPicksCount = this.getValidPicksCount(tokenData, selectedPicks);

                        if (validPicksCount.length >= tokenData.minimumLegs! - 1) {
                            return new ErrorNotification(tokenData, AccaBoostError.MinOdds, this.accaBoostConfig.errorNotificationDuration, true);
                        }
                    }
                }

                if (!isUndefined(current.token) && !current.validCombo) {
                    return null;
                }

                if (
                    !isUndefined(previous.token) &&
                    (previous.isSelected || this.accaBoostSelectOneLegAway(eligibilityState, previous, current)) &&
                    isUndefined(current.token) &&
                    current.picksCount > 0 &&
                    currentBetslipType === BetslipType.Combo &&
                    current.validCombo
                ) {
                    const error = this.resolveError(eligibilityState, previous);

                    return error ? new ErrorNotification(previous.token, error, this.accaBoostConfig.errorNotificationDuration) : null;
                }

                if (!isUndefined(current.token) && current.isSelected) {
                    if (maxRewardAchieved) {
                        return new MaxRewardNotification(current.token, this.accaBoostConfig.maxRewardNotificationDuration, current.picksCount);
                    }

                    if (!current.maxReward) {
                        const tokenSwitched = !isUndefined(previous.token) && previous.isSelected && previous.token.id !== current.token.id;

                        if (pickCountChanged) {
                            return new LadderNotification({
                                token: current.token,
                                tokenSwitched,
                                picksCount: current.picksCount,
                                duration: this.accaBoostConfig.notificationDuration,
                            });
                        }

                        if (isUndefined(previous.token) || !previous.isSelected) {
                            return new LadderNotification({
                                token: current.token,
                                tokenSwitched,
                                picksCount: current.picksCount,
                                duration: this.accaBoostConfig.notificationDuration,
                            });
                        }
                    }
                }

                return null;
            }),
            distinctUntilChanged(),
            share(),
        );
    }

    private getValidPicksCount(tokenData: AccaBoostToken, selectedPicks: BetslipPick[]) {
        const minOdds = this.mapMinOdds(tokenData.filter.minOddsPerLeg);

        return selectedPicks.filter((t) => {
            const oddsComparer = this.oddsComparerFactory.createComparer(t.currentPrice!.nativeOdds);

            return oddsComparer.greaterOrEqualThan(minOdds);
        });
    }

    private mapMinOdds(minOddsPerLeg: AccaBoostTokenMinOdds): Odds {
        return OddsOperations.createOdds({
            odds: minOddsPerLeg.european,
            numerator: minOddsPerLeg.britishNumerator,
            denominator: minOddsPerLeg.britishDenominator,
            americanOdds: minOddsPerLeg.american,
        });
    }

    private resolveError(eligibilityState: IRewardTokenEligibilityState, previous: AccaBoostContext): AccaBoostError | undefined {
        const invalidHardCriteria =
            eligibilityState.comboBet.find((e) => e.tokenId === previous.token?.userTokenId)?.invalidHardCriteria || undefined;

        if (!invalidHardCriteria) {
            return;
        }

        return ErrorsMap[invalidHardCriteria];
    }

    private resolveErrorForOtherTypes(
        state: IRewardTokenEligibilityState,
        current: AccaBoostContext,
        type: BetslipType.Single | BetslipType.Teaser | BetslipType.System,
    ): AccaBoostError | undefined {
        if (type === BetslipType.System) {
            return ErrorsMap.SlipType;
        }

        const eligibilities = this.getElibilities(state, type);

        if (!eligibilities) {
            return undefined;
        }

        const invalidHardCriteria = eligibilities.find((e) => e.tokenId === current.token?.userTokenId)?.invalidHardCriteria || undefined;

        if (!invalidHardCriteria) {
            return;
        }

        return ErrorsMap[invalidHardCriteria];
    }

    private getElibilities(
        eligibilityState: IRewardTokenEligibilityState,
        type: BetslipType.Single | BetslipType.Teaser,
    ): IRewardTokenEligibility[] | undefined {
        if (type === BetslipType.Single) {
            const eligibilityStates = Object.values(eligibilityState.singleBet);

            return eligibilityStates.length > 0 ? eligibilityStates[0] : undefined;
        }

        return eligibilityState.teaserBet;
    }

    private accaBoostSelectOneLegAway(
        eligibilityState: IRewardTokenEligibilityState,
        previous: AccaBoostContext,
        current: AccaBoostContext,
    ): boolean {
        const invalidHardCriteria = eligibilityState.comboBet.find(
            (tokenEligible) => tokenEligible.tokenId === previous.token?.userTokenId,
        )?.invalidHardCriteria;

        return (
            (!isUndefined(invalidHardCriteria) &&
                !isUndefined(previous.token) &&
                !isUndefined(previous.token.minimumLegs) &&
                previous.token.minimumLegs === current.picksCount) ||
            false
        );
    }
}
